import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Download, RefreshCw, AlertTriangle, 
  Clock, User, Globe, Smartphone, Monitor, Bug,
  ChevronDown, ChevronUp, Eye, Trash2, Archive,
  Calendar, MapPin, Code, Database, Server
} from 'lucide-react';
import { logger } from '../../lib/logger';
import { supabase } from '../../lib/supabase';

interface ErrorLogEntry {
  id: string;
  timestamp: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'CRITICAL';
  message: string;
  context?: string;
  data?: any;
  userId?: string;
  sessionId?: string;
  userAgent?: string;
  url?: string;
  stack?: string;
  user?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

const ErrorLog = () => {
  const [errors, setErrors] = useState<ErrorLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterContext, setFilterContext] = useState('all');
  const [dateRange, setDateRange] = useState('24h');
  const [selectedErrors, setSelectedErrors] = useState<string[]>([]);
  const [expandedError, setExpandedError] = useState<string | null>(null);

  // Fetch errors from multiple sources
  useEffect(() => {
    const fetchErrors = async () => {
      try {
        setLoading(true);
        
        // Get errors from logger
        const loggerErrors = logger.getLogs().map((log, index) => ({
          id: `logger-${index}`,
          timestamp: log.timestamp,
          level: ['DEBUG', 'INFO', 'WARN', 'ERROR', 'CRITICAL'][log.level] as any,
          message: log.message,
          context: log.context,
          data: log.data,
          userId: log.userId,
          sessionId: log.sessionId,
          userAgent: log.userAgent,
          url: log.url,
          stack: log.stack
        }));

        // Get critical errors from localStorage
        const criticalErrors = JSON.parse(localStorage.getItem('app_critical_errors') || '[]')
          .map((error: any, index: number) => ({
            id: `critical-${index}`,
            timestamp: error.timestamp,
            level: 'CRITICAL' as const,
            message: error.message,
            context: 'ErrorBoundary',
            data: error,
            stack: error.stack,
            url: error.url,
            userAgent: error.userAgent
          }));

        // Get error logs from localStorage
        const errorLogs = JSON.parse(localStorage.getItem('app_error_logs') || '[]')
          .map((error: any, index: number) => ({
            id: `error-${index}`,
            timestamp: error.timestamp,
            level: error.level || 'ERROR' as const,
            message: error.message,
            context: error.context,
            data: error.data,
            userId: error.userId,
            sessionId: error.sessionId,
            userAgent: error.userAgent,
            url: error.url,
            stack: error.stack
          }));

        // Try to fetch from database if available
        let dbErrors: ErrorLogEntry[] = [];
        try {
          const { data: securityLogs, error: securityError } = await supabase
            .from('security_audit_log')
            .select(`
              *
            `)
            .eq('success', false)
            .order('created_at', { ascending: false })
            .limit(100);

          if (!securityError && securityLogs) {
            dbErrors = securityLogs.map(log => ({
              id: log.id,
              timestamp: log.created_at,
              level: 'ERROR' as const,
              message: `Security event: ${log.action} on ${log.resource}`,
              context: 'Security',
              data: log.details,
              userId: log.user_id,
              userAgent: log.user_agent,
              url: log.resource,
              user: undefined
            }));
          }
        } catch (dbError) {
          console.warn('Could not fetch database errors:', dbError);
        }

        // Combine all errors and sort by timestamp
        const allErrors = [...loggerErrors, ...criticalErrors, ...errorLogs, ...dbErrors]
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

        setErrors(allErrors);
      } catch (err) {
        console.error('Error fetching error logs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchErrors();
  }, []);

  // Filter errors based on search and filters
  const filteredErrors = errors.filter(error => {
    const matchesSearch = error.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (error.context && error.context.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (error.url && error.url.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesLevel = filterLevel === 'all' || error.level === filterLevel;
    const matchesContext = filterContext === 'all' || error.context === filterContext;
    
    // Date range filter
    const errorDate = new Date(error.timestamp);
    const now = new Date();
    let matchesDate = true;
    
    switch (dateRange) {
      case '1h':
        matchesDate = now.getTime() - errorDate.getTime() <= 60 * 60 * 1000;
        break;
      case '24h':
        matchesDate = now.getTime() - errorDate.getTime() <= 24 * 60 * 60 * 1000;
        break;
      case '7d':
        matchesDate = now.getTime() - errorDate.getTime() <= 7 * 24 * 60 * 60 * 1000;
        break;
      case '30d':
        matchesDate = now.getTime() - errorDate.getTime() <= 30 * 24 * 60 * 60 * 1000;
        break;
    }
    
    return matchesSearch && matchesLevel && matchesContext && matchesDate;
  });

  // Get unique contexts for filter
  const contexts = Array.from(new Set(errors.map(e => e.context).filter(Boolean)));

  const handleSelectError = (errorId: string) => {
    setSelectedErrors(prev => 
      prev.includes(errorId) 
        ? prev.filter(id => id !== errorId)
        : [...prev, errorId]
    );
  };

  const handleSelectAll = () => {
    setSelectedErrors(
      selectedErrors.length === filteredErrors.length 
        ? [] 
        : filteredErrors.map(error => error.id)
    );
  };

  const handleClearLogs = () => {
    if (confirm('Are you sure you want to clear all error logs? This action cannot be undone.')) {
      logger.clearLogs();
      localStorage.removeItem('app_critical_errors');
      localStorage.removeItem('app_error_logs');
      setErrors([]);
      setSelectedErrors([]);
    }
  };

  const handleExportLogs = () => {
    const exportData = {
      exportDate: new Date().toISOString(),
      totalErrors: filteredErrors.length,
      errors: filteredErrors
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'CRITICAL':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Critical
        </span>;
      case 'ERROR':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Bug className="h-3 w-3 mr-1" />
          Error
        </span>;
      case 'WARN':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Warning
        </span>;
      case 'INFO':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Info
        </span>;
      case 'DEBUG':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Debug
        </span>;
      default:
        return null;
    }
  };

  const getContextIcon = (context: string) => {
    switch (context?.toLowerCase()) {
      case 'auth':
      case 'authentication':
        return <User className="h-4 w-4 text-blue-600" />;
      case 'api':
      case 'supabase':
        return <Database className="h-4 w-4 text-green-600" />;
      case 'security':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'performance':
        return <Monitor className="h-4 w-4 text-purple-600" />;
      case 'errorboundary':
        return <Bug className="h-4 w-4 text-orange-600" />;
      default:
        return <Code className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getDeviceInfo = (userAgent: string) => {
    if (!userAgent) return { device: 'Unknown', browser: 'Unknown' };
    
    const isMobile = /Mobile|Android|iPhone|iPad/.test(userAgent);
    const isTablet = /iPad|Tablet/.test(userAgent);
    
    let browser = 'Unknown';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    
    const device = isTablet ? 'Tablet' : isMobile ? 'Mobile' : 'Desktop';
    
    return { device, browser };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Error Log</h1>
              <p className="text-gray-600">Monitor and analyze application errors</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
              <button
                onClick={handleExportLogs}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
              <button
                onClick={handleClearLogs}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>Clear Logs</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search errors by message, context, or URL..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Levels</option>
                <option value="CRITICAL">Critical</option>
                <option value="ERROR">Error</option>
                <option value="WARN">Warning</option>
                <option value="INFO">Info</option>
                <option value="DEBUG">Debug</option>
              </select>
            </div>
            <div>
              <select
                value={filterContext}
                onChange={(e) => setFilterContext(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Contexts</option>
                {contexts.map(context => (
                  <option key={context} value={context}>{context}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="1h">Last Hour</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Critical</p>
                <p className="text-2xl font-bold text-gray-900">
                  {errors.filter(e => e.level === 'CRITICAL').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <Bug className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Errors</p>
                <p className="text-2xl font-bold text-gray-900">
                  {errors.filter(e => e.level === 'ERROR').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Warnings</p>
                <p className="text-2xl font-bold text-gray-900">
                  {errors.filter(e => e.level === 'WARN').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Last 24h</p>
                <p className="text-2xl font-bold text-gray-900">
                  {errors.filter(e => {
                    const errorDate = new Date(e.timestamp);
                    const now = new Date();
                    return now.getTime() - errorDate.getTime() <= 24 * 60 * 60 * 1000;
                  }).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Server className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{errors.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Log Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Error Entries ({filteredErrors.length})
              </h2>
              {selectedErrors.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedErrors.length} selected</span>
                  <button className="text-sm text-red-600 hover:text-red-700 font-medium">
                    Delete Selected
                  </button>
                  <button className="text-sm text-gray-600 hover:text-gray-700 font-medium">
                    Archive Selected
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : filteredErrors.length === 0 ? (
            <div className="text-center py-12">
              <Bug className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Errors Found</h3>
              <p className="text-gray-600 mb-4">
                {searchQuery || filterLevel !== 'all' || filterContext !== 'all'
                  ? 'No errors match your current filters'
                  : 'No errors have been logged yet'}
              </p>
              {(searchQuery || filterLevel !== 'all' || filterContext !== 'all') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilterLevel('all');
                    setFilterContext('all');
                  }}
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedErrors.length === filteredErrors.length && filteredErrors.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Context
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredErrors.map((error) => {
                    const deviceInfo = getDeviceInfo(error.userAgent || '');
                    const isExpanded = expandedError === error.id;
                    
                    return (
                      <React.Fragment key={error.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedErrors.includes(error.id)}
                              onChange={() => handleSelectError(error.id)}
                              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-6 py-4">
                            {getLevelBadge(error.level)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-md">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {error.message}
                              </div>
                              {error.url && (
                                <div className="text-xs text-gray-500 truncate">
                                  {error.url}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              {getContextIcon(error.context || '')}
                              <span className="text-sm text-gray-900">
                                {error.context || 'Unknown'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {error.user ? (
                                <div>
                                  <div>{error.user.first_name} {error.user.last_name}</div>
                                  <div className="text-xs text-gray-500">{error.user.email}</div>
                                </div>
                              ) : error.userId ? (
                                <div className="text-xs text-gray-500">ID: {error.userId}</div>
                              ) : (
                                <span className="text-gray-400">Anonymous</span>
                              )}
                            </div>
                            {error.userAgent && (
                              <div className="flex items-center space-x-1 mt-1">
                                {deviceInfo.device === 'Mobile' ? (
                                  <Smartphone className="h-3 w-3 text-gray-400" />
                                ) : (
                                  <Monitor className="h-3 w-3 text-gray-400" />
                                )}
                                <span className="text-xs text-gray-500">
                                  {deviceInfo.browser}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {formatTimestamp(error.timestamp)}
                            </div>
                            {error.sessionId && (
                              <div className="text-xs text-gray-500">
                                Session: {error.sessionId.slice(0, 8)}...
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => setExpandedError(isExpanded ? null : error.id)}
                              className="text-gray-400 hover:text-gray-600"
                              title={isExpanded ? "Collapse" : "Expand"}
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                        
                        {/* Expanded Error Details */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="px-6 py-4 bg-gray-50">
                              <div className="space-y-4">
                                {/* Error Details */}
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2">Error Details</h4>
                                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="font-medium text-gray-700">Message:</span>
                                        <p className="text-gray-900 mt-1">{error.message}</p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">Context:</span>
                                        <p className="text-gray-900 mt-1">{error.context || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">URL:</span>
                                        <p className="text-gray-900 mt-1 break-all">{error.url || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">User Agent:</span>
                                        <p className="text-gray-900 mt-1 break-all">{error.userAgent || 'N/A'}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Additional Data */}
                                {error.data && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Additional Data</h4>
                                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                                      <pre className="text-xs text-gray-700 overflow-x-auto">
                                        {JSON.stringify(error.data, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}

                                {/* Stack Trace */}
                                {error.stack && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Stack Trace</h4>
                                    <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs overflow-x-auto">
                                      <pre>{error.stack}</pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Error Summary */}
        {errors.length > 0 && (
          <div className="bg-orange-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Error Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Most Common Contexts</h4>
                <div className="space-y-1">
                  {contexts.slice(0, 5).map(context => {
                    const count = errors.filter(e => e.context === context).length;
                    return (
                      <div key={context} className="flex justify-between text-sm">
                        <span className="text-gray-600">{context}</span>
                        <span className="font-medium text-gray-900">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Error Distribution</h4>
                <div className="space-y-1">
                  {['CRITICAL', 'ERROR', 'WARN', 'INFO', 'DEBUG'].map(level => {
                    const count = errors.filter(e => e.level === level).length;
                    const percentage = errors.length > 0 ? ((count / errors.length) * 100).toFixed(1) : '0';
                    return (
                      <div key={level} className="flex justify-between text-sm">
                        <span className="text-gray-600">{level}</span>
                        <span className="font-medium text-gray-900">{count} ({percentage}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Trends</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>Last hour: {errors.filter(e => {
                    const errorDate = new Date(e.timestamp);
                    const now = new Date();
                    return now.getTime() - errorDate.getTime() <= 60 * 60 * 1000;
                  }).length} errors</p>
                  <p>Last 24 hours: {errors.filter(e => {
                    const errorDate = new Date(e.timestamp);
                    const now = new Date();
                    return now.getTime() - errorDate.getTime() <= 24 * 60 * 60 * 1000;
                  }).length} errors</p>
                  <p>Critical errors: {errors.filter(e => e.level === 'CRITICAL').length}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorLog;